import {Component, EventEmitter, Input, OnInit, ViewChild} from '@angular/core';
import {EnvService} from "../services/env.service";
import {HttpClient, HttpParams} from "@angular/common/http";
import {BaseComponent} from "../base/base.component";
import {ActivatedRoute, Router, Params} from '@angular/router';
import {AlertService} from "../services/alert.service";
import {AuthService} from "../services/auth.service";
import {debounceTime} from "rxjs/operators";
import {MicroAceEditorComponent} from "../shared/ace-editor/micro-ace-editor.component";

@Component({
  selector: 'micro-chatApp-detail',
  templateUrl: './chatApp-detail.component.html'
})
export class ChatAppDetailComponent implements OnInit {

  @ViewChild('descriptorEditor', {static: false}) descriptorEditor: MicroAceEditorComponent;

  @Input()
  chatAppId: any;
  chatApp: any;
  selectedEvent: any;
  parsedDescriptorError: any;
  previousTabIndex: number = 0;
  selectedTabIndex: number = 0;
  selectedMenuId: any;
  selectedMenu: any;
  newAction: any;
  descriptorChanged: EventEmitter<any> = new EventEmitter<any>();
  debug: boolean = true;
  sankeyOptions;
  counterOptions;
  barOptions;
  newEventFilter: any;
  descriptorTemplate = {
    startMenuId: "start",
    vars: {},
    menus: [
      {
        id: "start",
        message: "Hello ${msisdn}\\nSend me something.",
        counter: undefined,
        var: "hello",
        shouldClose: false,
        actions: [
          {
            pattern: ".*",
            menuId: "exit",
            counter: undefined
          }
        ],
      },
      {
        id: "exit",
        message: "You send ${hello}\\nGoodbye ${msisdn}",
        shouldClose: true
      }
    ]
  };
  parsedDescriptor: any;

  patternScriptStart = /^\s*\(\s*\)\s*\{[\r\n]*/s;
  patternScriptEnd = /\s*[\r\n]*\}\s*$/s;

  constructor(protected env: EnvService,
              protected http: HttpClient,
              private route: ActivatedRoute,
              private router: Router,
              protected authService: AuthService,
              private alertService: AlertService) {
  }

  ngOnInit() {
    this.resetNewEventFilter();
    if (this.chatAppId) {
      this.load(this.chatAppId);
    } else {
      this.route.params
        .subscribe((params: Params) => {
          let id: any = params['id'];
          if (id === '0') {
            this.createNew();
          } else {
            this.load(id);
          }
        });
    }

    this.descriptorChanged.pipe().pipe(debounceTime(500)).subscribe(change => {
      this.handleDescriptorChanged();
    });
  }

  load(id: any) {
    this.http.get(`${this.env.e.url}/ai/chatApps/${id}`).subscribe(
      data => {
        this.setChatApp(data)
      }
    );
  }

  setChatApp(chatApp: any) {
    this.chatApp = chatApp;
    this.onDescriptorChanged();
    this.reloadCounters();
  }

  createNew() {
    this.chatApp = {
      callbackUrl: undefined,
      useDescriptor: true,
      errorRspMessage: "Internal Error",
      eventFilters: [],
      descriptor: JSON.stringify(this.descriptorTemplate, null, 2)
    };
  }

  createNewAction() {
    this.newAction = {}
  }

  resetNewEventFilter() {
    this.newEventFilter = {
      filter: 'true'
    }
  }

  removeEventFilter(i) {
    this.chatApp.eventFilters.splice(i, 1);
  }

  addNewEventFilter() {
    this.chatApp.eventFilters.push(this.newEventFilter);
    this.resetNewEventFilter();
  }

  save() {
    this.cleanParsedDescriptor();
    if (!this.chatApp.id) {
      this.http.post(`${this.env.e.url}/ai/chatApps`, this.chatApp)
        .subscribe(
          data => {
            this.setChatApp(data)
            this.alertService.info(`Created ${this.chatApp.name}`);
          }
        );
    } else {
      this.http.put(`${this.env.e.url}/ai/chatApps/${this.chatApp.id}`, this.chatApp)
        .subscribe(
          data => {
            this.setChatApp(data)
            this.alertService.info(`Updated ${this.chatApp.name}`);
          }
        );
    }
  }

  delete() {
    this.http.delete(`${this.env.e.url}/ai/chatApps/${this.chatApp.id}`)
      .subscribe(
        complete => {
          this.alertService.info(`Deleted ${this.chatApp.name}`);
          this.router.navigate(["/chatApps"]);
        }
      );
  }

  onEventTypeChange() {
    this.selectedEvent = null
    this.chatApp.eventSource = undefined;
  }

  onSelectedTabIndexChanged() {
    if (this.selectedTabIndex == 2 || this.selectedTabIndex == 3) {
      this.parseDescriptor();
    } else if (this.previousTabIndex == 2) {
      if (this.parsedDescriptor) {
        this.cleanParsedDescriptor();
      }
    }
    this.previousTabIndex = this.selectedTabIndex;
  }

  cleanParsedDescriptor() {
    for (let menu of this.parsedDescriptor.menus) {
      if (menu.behavior?.task) {
        this.visitParsedDescriptorTask(menu.behavior.task);
      }
    }
    this.updateMessageFromScript(this.selectedMenu);
    this.updateResponseScriptFromScript(this.selectedMenu);
    this.chatApp.descriptor = JSON.stringify(this.parsedDescriptor, null, 2);
  }

  visitParsedDescriptorTask(task) {
    delete task.parent;
    delete task.layout;
    delete task.taskDef;

    if (!task.debug) {
      delete task.debug;
    }

    if (!task.collapsed) {
      delete task.collapsed;
    }

    if (!task.collapseAll) {
      delete task.collapseAll;
    }
    delete task.order;
    delete task.reparenting;

    if (task.enabled) {
      delete task.enabled;
    }

    if (Object.keys(task.params).length == 0) {
      delete task.params;
    }

    if (task.children) {
      if (task.children.length == 0) {
        delete task.children;
      } else {
        for (let child of task.children) {
          this.visitParsedDescriptorTask(child);
        }
      }
    }
  }

  onDescriptorChanged() {
    this.descriptorChanged.emit(this.chatApp.descriptor);
  }

  handleDescriptorChanged() {
    this.parseDescriptor();
  }

  escapeMultilineStrings(jsonStr) {
    return jsonStr.replace(
      /"(?:\\.|[^"\\])*"/g, // Match all double-quoted strings
      (match) => match.replace(/\r?\n/g, '\\n') // Replace newlines inside them
    );
  }

  parseDescriptor() {
    try {
      this.parsedDescriptor = JSON.parse(this.escapeMultilineStrings(this.chatApp.descriptor));
      if (this.parsedDescriptor.menus) {
        for (let menu of this.parsedDescriptor.menus) {
          if (menu.behavior?.task) {
            this.visit(menu.behavior.task);
          }
        }
      }
      this.parsedDescriptorError = undefined;
      if (!this.selectedMenuId) {
        this.selectedMenuId = this.parsedDescriptor.startMenuId;
      }
      this.onSelectedMenuIdChanged();
      this.validateParsedDescriptor();
    } catch (e) {
      if (this.debug) {
        console.error(e);
      }
      this.parsedDescriptorError = e;
      this.parsedDescriptor = undefined;
    }
  }

  validateParsedDescriptor() {
    if (!this.descriptorEditor) {
      return;
    }
  }

  visit(task) {
    if (!task) {
      return;
    }

    if (task.enabled === undefined) {
      task.enabled = true;
    }

    if (task.children) {
      for (let child of task.children) {
        this.visit(child);
      }
    }
  }

  onSelectedMenuIdChanged() {

    if (this.selectedMenu) {
      this.updateMessageFromScript(this.selectedMenu);
    }

    this.createNewAction();
    if (!this.selectedMenuId) {
      this.selectedMenu = undefined;
      return;
    }
    for (let menu of this.parsedDescriptor.menus) {
      if (menu.id === this.selectedMenuId) {
        if (menu.behavior) {
          menu.type = 'Behavior';
        } else {
          menu.type = this.isScript(menu.message) ? 'Script' : 'Message';

          if (!menu.actions) {
            menu.actions = [];
          }
        }
        this.updateScriptFromMessage(menu);
        this.updateScriptFromResponseScript(menu);
        this.selectedMenu = menu;
        return;
      }
    }
    this.selectedMenu = undefined;
  }

  removeAction(menu, i) {
    menu.actions.splice(i, 1);
  }

  addAction() {
    this.selectedMenu.actions.push(this.newAction);
    this.createNewAction();
  }

  createDepWheelOptions(data) {
    return {
      title: {
        text: null,
      },
      chart: {
        animation: false,
        type: 'dependencywheel',
        height: 600,
        width: 600
      },
      credits: {
        enabled: false
      },
      series: [{
        keys: ['from', 'to', 'weight'],
        data: data,
        type: 'dependencywheel'
      }]
    };
  }

  createCounterOptions(data): any[] {
    var options = [];
    for (let entry of data) {
      options.push({
        title: {
          text: null,
        },
        plotOptions: {
          pie: {
            allowPointSelect: true,
            cursor: 'pointer',
            dataLabels: {
              enabled: true,
              format: '<b>{point.name}</b>: (<em>{point.y}</em>) {point.percentage:.1f}%'
            }
          }
        },
        chart: {
          animation: true,
          type: 'pie',
          height: 500,
          width: 500
        },
        credits: {
          enabled: false
        },
        data: entry.data,
        name: entry.name,
        series: [entry]
      });
    }
    return options;
  }

  createBarOptions(data): any[] {
    var options = [];
    for (let entry of data) {
      var categories = [];
      var values = [];
      for (let e of entry.data) {
        categories.push(e.name);
        values.push(e.y);
      }
      options.push({
        title: {
          text: null,
        },
        xAxis: {
          categories: categories,
          title: {
            text: entry.name
          }
        },
        chart: {
          animation: true,
          type: 'bar',
          height: 500
        },
        credits: {
          enabled: false
        },
        data: entry.data,
        name: entry.name,
        series: [{
          name: entry.name,
          data: values
        }]
      });
    }
    return options;
  }

  reloadCounters() {
    var params: HttpParams = new HttpParams()
      .set("chatAppId", this.chatApp.id)
    ;
    this.http.get(`${this.env.e.url}/ai/chatSessionLogs/sankey`, {params: params}).subscribe(
      data => {
        this.sankeyOptions = this.createDepWheelOptions(data);
      }
    );
    this.http.get(`${this.env.e.url}/ai/chatApps/counters`, {params: params}).subscribe(
      data => {
        this.counterOptions = this.createCounterOptions(data);
        this.barOptions = this.createBarOptions(data);
      }
    );
  }

  private isScript(message) {
    return this.patternScriptStart.test(message) && this.patternScriptEnd.test(message);
  }

  private updateScriptFromMessage(menu: any) {
    if (menu && menu.type === 'Script') {
      menu.scriptMessage = menu.message.toString()
        .replace(this.patternScriptStart, "")
        .replace(this.patternScriptEnd, "");
    } else {
      menu.scriptMessage = undefined;
    }
  }

  private updateMessageFromScript(menu: any) {
    if (menu && menu.scriptMessage && menu.type === 'Script') {
      menu.message = "(){\n" + menu.scriptMessage + "\n}";
      menu.scriptMessage = undefined;
    }
  }

  private updateScriptFromResponseScript(menu: any) {
    if (menu.responseScript) {
      menu.scriptResponseScript = menu.responseScript.toString()
        .replace(this.patternScriptStart, "")
        .replace(this.patternScriptEnd, "");
    } else {
      menu.scriptResponseScript = undefined;
    }
  }

  private updateResponseScriptFromScript(menu: any) {
    if (menu.scriptResponseScript) {
      menu.responseScript = "(){\n" + menu.scriptResponseScript + "\n}";
      menu.scriptResponseScript = undefined;
    } else {
      menu.scriptResponseScript = undefined;
      menu.responseScript = undefined;
    }
  }
}
